import React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Typography, Button, Grid, TextField, Select, MenuItem,
  FormControl, InputLabel, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Paper
} from '@material-ui/core'
import { Edit as EditIcon, Warning as WarningIcon } from '@material-ui/icons'

const cardStyle = {
  backgroundColor: '#fff', borderRadius: 8,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 16
}

const FollowUpPanel = (props) => {
  const { projectId, project, url, users, onRefresh } = props
  const fu = project?.followUp || {}
  const [editing, setEditing] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [losing, setLosing] = React.useState(false)
  const [lostReason, setLostReason] = React.useState('')
  const [winning, setWinning] = React.useState(false)

  // Edit form state
  const [presenter, setPresenter] = React.useState(fu.presenter || '')
  const [presenterName, setPresenterName] = React.useState(fu.presenterName || '')
  const [followUpOwner, setFollowUpOwner] = React.useState(fu.followUpOwner || '')
  const [followUpName, setFollowUpName] = React.useState(fu.followUpName || '')
  const [expectedCloseDate, setExpectedCloseDate] = React.useState(fu.expectedCloseDate ? fu.expectedCloseDate.slice(0, 10) : '')
  const [cadenceDays, setCadenceDays] = React.useState(fu.cadenceDays || 7)
  const [repricingDays, setRepricingDays] = React.useState(fu.repricingThresholdDays || 45)

  const isSubmitted = project?.status === 'submitted'
  const isReview = project?.status === 'review'
  const isWon = project?.status === 'won'
  const isLost = project?.status === 'lost'

  const daysSinceSubmission = fu.submittedAt
    ? Math.floor((Date.now() - new Date(fu.submittedAt).getTime()) / 86400000)
    : 0
  const isStale = isSubmitted && fu.submittedAt && daysSinceSubmission >= (fu.repricingThresholdDays || 45)

  const handleSaveFollowUp = () => {
    setSaving(true)
    axios.put(`${url}/api/bid-estimation/projects/${projectId}/follow-up`, {
      presenter, presenterName, followUpOwner, followUpName,
      expectedCloseDate: expectedCloseDate || null,
      cadenceDays, repricingThresholdDays: repricingDays
    }).then(() => {
      setSaving(false)
      setEditing(false)
      onRefresh()
    }).catch(err => { console.error(err); setSaving(false) })
  }

  const handleUserSelect = (setter, nameSetter) => (e) => {
    const userId = e.target.value
    const user = (users || []).find(u => u._id === userId)
    setter(userId)
    nameSetter(user?.name || '')
  }

  const handleWin = () => {
    setWinning(true)
    axios.post(`${url}/api/bid-estimation/projects/${projectId}/close/win`).then(() => {
      setWinning(false)
      onRefresh()
    }).catch(err => { console.error(err); setWinning(false) })
  }

  const handleLoss = () => {
    axios.post(`${url}/api/bid-estimation/projects/${projectId}/close/loss`, { lostReason }).then(() => {
      setLosing(false)
      setLostReason('')
      onRefresh()
    }).catch(err => { console.error(err); setLosing(false) })
  }

  const userList = users || []

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>Follow-Up</Typography>
        {(isSubmitted || isReview) && (
          <IconButton size="small" onClick={() => setEditing(!editing)}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </div>

      {/* Info grid */}
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Typography variant="caption" color="textSecondary">Presenter</Typography>
          <Typography variant="body2">{fu.presenterName || 'Not set'}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="textSecondary">Follow-up Owner</Typography>
          <Typography variant="body2">{fu.followUpName || 'Not set'}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="textSecondary">Expected Close</Typography>
          <Typography variant="body2">
            {fu.expectedCloseDate ? new Date(fu.expectedCloseDate).toLocaleDateString() : 'Not set'}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="textSecondary">Cadence</Typography>
          <Typography variant="body2">Every {fu.cadenceDays || 7} days</Typography>
        </Grid>
      </Grid>

      {/* Stale warning */}
      {isStale && (
        <Paper style={{ backgroundColor: '#fff8e1', padding: '8px 12px', marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <WarningIcon style={{ color: '#ff9800', fontSize: 18 }} />
          <Typography variant="body2" style={{ fontSize: 12 }}>
            Estimate is {daysSinceSubmission} days old — consider repricing before close.
          </Typography>
        </Paper>
      )}

      {/* Win/Loss buttons */}
      {isSubmitted && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button size="small" variant="contained" onClick={handleWin} disabled={winning}
            style={{ backgroundColor: '#2e7d32', color: '#fff', textTransform: 'none', fontSize: 12 }}>
            {winning ? 'Marking...' : 'Mark Won'}
          </Button>
          <Button size="small" variant="outlined" onClick={() => setLosing(true)}
            style={{ borderColor: '#f44336', color: '#f44336', textTransform: 'none', fontSize: 12 }}>
            Mark Lost
          </Button>
        </div>
      )}

      {/* Won/Lost banner */}
      {isWon && fu.wonAt && (
        <Paper style={{ backgroundColor: '#e8f5e9', padding: '8px 12px', marginTop: 8 }}>
          <Typography variant="body2" style={{ color: '#2e7d32', fontSize: 12 }}>
            Won on {new Date(fu.wonAt).toLocaleDateString()}
          </Typography>
        </Paper>
      )}
      {isLost && fu.lostAt && (
        <Paper style={{ backgroundColor: '#ffebee', padding: '8px 12px', marginTop: 8 }}>
          <Typography variant="body2" style={{ color: '#f44336', fontSize: 12 }}>
            Lost on {new Date(fu.lostAt).toLocaleDateString()}
            {fu.lostReason && ` — ${fu.lostReason}`}
          </Typography>
        </Paper>
      )}

      {/* Edit form */}
      {editing && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #eee' }}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Presenter</InputLabel>
                <Select value={presenter} onChange={handleUserSelect(setPresenter, setPresenterName)} label="Presenter">
                  <MenuItem value="">None</MenuItem>
                  {userList.map(u => <MenuItem key={u._id} value={u._id}>{u.name || u._id}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Follow-up Owner</InputLabel>
                <Select value={followUpOwner} onChange={handleUserSelect(setFollowUpOwner, setFollowUpName)} label="Follow-up Owner">
                  <MenuItem value="">None</MenuItem>
                  {userList.map(u => <MenuItem key={u._id} value={u._id}>{u.name || u._id}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" variant="outlined" label="Expected Close" type="date"
                value={expectedCloseDate} onChange={e => setExpectedCloseDate(e.target.value)}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" variant="outlined" label="Cadence (days)" type="number"
                value={cadenceDays} onChange={e => setCadenceDays(parseInt(e.target.value) || 7)} />
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" variant="outlined" label="Reprice (days)" type="number"
                value={repricingDays} onChange={e => setRepricingDays(parseInt(e.target.value) || 45)} />
            </Grid>
          </Grid>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Button size="small" variant="contained" color="primary" onClick={handleSaveFollowUp} disabled={saving}
              style={{ textTransform: 'none', fontSize: 12 }}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button size="small" onClick={() => setEditing(false)} style={{ textTransform: 'none', fontSize: 12 }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Loss reason dialog */}
      <Dialog open={losing} onClose={() => setLosing(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark Project Lost</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Reason for loss" multiline rows={3} variant="outlined"
            value={lostReason} onChange={e => setLostReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLosing(false)}>Cancel</Button>
          <Button onClick={handleLoss} variant="contained" color="secondary">Mark Lost</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  users: state.users || []
})

export default connect(mapStateToProps)(FollowUpPanel)
